import React, { Fragment, useRef, useState } from 'react';
import { useDrag } from 'react-use-gesture';
import { animated } from '@react-spring/web';
import type { SpringValue } from '@react-spring/web';

import DisableUserSelect from '@src/components/DisableUserSelect';
import ForceCursor from '@src/components/ForceCursor';

type TProps = {
  children?: React.ReactNode;
  forceCursor?: boolean;
  height: SpringValue<number>;
  width: SpringValue<number>;
  left: SpringValue<number>;
  top: SpringValue<number>;
  paddedHeight: SpringValue<number>;
  paddedWidth: SpringValue<number>;
  paddedLeft: SpringValue<number>;
  paddedTop: SpringValue<number>;
  pageItemId: string;
  position: string;
  zIndex: number;
  onClick?: () => void;
  onMouseDown?: () => void;
  onDrag: (
    pos: string,
    movement: [number, number],
    isFirst: boolean,
    isLast: boolean
  ) => void;
  onDragStart: (position: string) => void;
  onDragStop: () => void;
};

export const HANDLE_POSITIONS = {
  e: 'HANDLE_POSITION_EAST',
  inner: 'HANDLE_POSITION_INNER',
  n: 'HANDLE_POSITION_NORTH',
  ne: 'HANDLE_POSITION_NORTH_EAST',
  nw: 'HANDLE_POSITION_NORTH_WEST',
  s: 'HANDLE_POSITION_SOUTH',
  se: 'HANDLE_POSITION_SOUTH_EAST',
  sw: 'HANDLE_POSITION_SOUTH_WEST',
  w: 'HANDLE_POSITION_WEST'
};

const DRAG_CURSORS = {
  [HANDLE_POSITIONS.e]: 'ew-resize',
  [HANDLE_POSITIONS.w]: 'ew-resize',
  [HANDLE_POSITIONS.n]: 'ns-resize',
  [HANDLE_POSITIONS.s]: 'ns-resize',

  [HANDLE_POSITIONS.ne]: 'nesw-resize',
  [HANDLE_POSITIONS.sw]: 'nesw-resize',

  [HANDLE_POSITIONS.nw]: 'nwse-resize',
  [HANDLE_POSITIONS.se]: 'nwse-resize',

  [HANDLE_POSITIONS.inner]: 'move'
};

function DragHandle({
  children,
  forceCursor = true,
  height,
  width,
  left,
  top,
  paddedHeight,
  paddedWidth,
  paddedLeft,
  paddedTop,
  zIndex,
  pageItemId,
  position,
  onClick,
  onMouseDown,
  onDrag,
  onDragStart,
  onDragStop
}: TProps): React.ReactElement {
  const lastMovementRef = useRef([0, 0]);
  const handleRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const dragBindings = useDrag((attrs) => {

    if (typeof $ === 'undefined') return;
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;
    if (!document.body) return;

    if (attrs.first) {
      if (onMouseDown) onMouseDown();
      onDragStart(position);
      setIsDragging(true);
    }

    const moved =
      lastMovementRef.current[0] !== attrs.movement[0] ||
      lastMovementRef.current[1] !== attrs.movement[1];

    if (moved || attrs.first || attrs.last) {
      onDrag(position, attrs.movement, attrs.first, attrs.last);
    }

    if (attrs.last) {
      onDragStop();
      if (onClick && !moved) onClick();
      setIsDragging(false);
    }
  });

  return (
    <animated.div
      ref={handleRef}
      style={{
        cursor: DRAG_CURSORS[position],
        left: paddedLeft,
        height: paddedHeight,
        position: 'absolute',
        top: paddedTop,
        width: paddedWidth,
        zIndex
      }}
      {...dragBindings()}
      data-drag-handle
      data-drag-target={pageItemId}
      touch-action="none"
    >
      {isDragging ? (
        <Fragment>
          <DisableUserSelect />
          {forceCursor ? <ForceCursor cursor={DRAG_CURSORS[position]} /> : null}
        </Fragment>
      ) : null}
      {children || (
        <animated.div
          style={{
            background: '#3091EC',
            left,
            height,
            position: 'absolute',
            top,
            width,
            zIndex
          }}
        />
      )}
    </animated.div>
  );
}

export default DragHandle;
